/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import LoadingButton from 'components/LoadingButton';
import PasswordTextField from 'components/PasswordTextField';
import { signInValidationSchema as validationSchema } from 'containers/shared/validationSchemas';
import type { SignInDto } from 'state/signIn';

import useStyles from './SignIn.style';

const useQuery = () => new URLSearchParams(useLocation().search);

type SignInProps = {
  signIn: (signInDto: SignInDto) => void;
  pending: boolean;
};

type SignInFormValues = {
  email: string;
  password: string;
  rememberLogin: boolean;
};

const defaultValues: SignInFormValues = {
  email: '',
  password: '',
  rememberLogin: false,
};

const SignIn: React.FC<SignInProps> = ({ signIn, pending }) => {
  const { signInForm, signInHeader, form, submit } = useStyles();
  const queryParams = useQuery();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignInFormValues>({
    mode: 'onTouched',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<SignInFormValues> = ({
    email: username,
    password,
    rememberLogin,
  }) => {
    const returnUrl = queryParams.get('ReturnUrl');
    signIn({ username, password, returnUrl, rememberLogin });
  };

  return (
    <div className={signInForm}>
      <div className={signInHeader}>
        <Typography variant="h2">Sign in</Typography>
        <Typography variant="body2">
          Don&apos;t have a MigrationWiz account?&nbsp;
          <Link
            to="/signup"
            variant="button"
            component={RouterLink}
            underline="always"
          >
            Sign up for free
          </Link>
        </Typography>
      </div>
      <form className={form} noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              variant="outlined"
              margin="normal"
              size="small"
              required
              fullWidth
              id="email"
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...field}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <PasswordTextField
              variant="outlined"
              margin="normal"
              size="small"
              required
              fullWidth
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...field}
            />
          )}
        />
        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={submit}
          pending={pending}
        >
          Sign In
        </LoadingButton>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <FormControlLabel
              // control={<Checkbox value="remember" color="primary" />}
              control={
                <Controller
                  name="rememberLogin"
                  control={control}
                  render={({ field }) => (
                    <Checkbox color="primary" {...field} />
                  )}
                />
              }
              label={
                <Typography variant="body2" color="textPrimary">
                  Keep me signed in
                </Typography>
              }
            />
          </Grid>
          <Grid item>
            <Link
              to="/forgotpassword"
              variant="button"
              underline="always"
              component={RouterLink}
            >
              Forgot password?
            </Link>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default SignIn;
